import React, { useEffect, useState } from 'react';

/**
 * Feb 2026 — OvertimeSlaChip
 *
 * Live countdown pill that turns amber then red as an approved
 * overtime slot approaches its end. Renders `null` when the row is
 * NOT the kind that needs a chip:
 *   * status !== 'approved'
 *   * no HH:MM window persisted on the row
 *   * the row's `date` is not today (Muscat)
 *   * the window has been over for more than 60 minutes (post-mortem
 *     display is only useful for a brief grace window — after that
 *     the History table's date is enough context)
 *
 * Colour ladder:
 *   * green   — window hasn't started yet ("Starts in H:MM")
 *   * blue    — inside the window with > 30 min remaining
 *   * amber   — inside the window with <= 30 min remaining
 *   * red     — inside the window with <= 10 min remaining
 *                OR past `end_time` (grace: 60 min)
 *
 * All time math is Muscat (UTC+4, no DST) to match the backend.
 */

const OFFICE_TZ_OFFSET_MS = 4 * 60 * 60 * 1000;
const toMuscat = (d = new Date()) => new Date(d.getTime() + OFFICE_TZ_OFFSET_MS);
const parseHHMM = (s) => {
  if (!s || typeof s !== 'string') return null;
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(s);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
};
const fmtHMinutes = (mins) => {
  if (mins <= 0) return '0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

export const OvertimeSlaChip = ({ row, dataTestPrefix = 'ot-sla' }) => {
  const [nowTick, setNowTick] = useState(() => new Date());

  useEffect(() => {
    // 15-second tick — smooth enough for a countdown, cheap enough
    // to leave running on a page with 30+ rows.
    const id = setInterval(() => setNowTick(new Date()), 15 * 1000);
    return () => clearInterval(id);
  }, []);

  if (!row || (row.status || '').toLowerCase() !== 'approved') return null;
  const startMin = parseHHMM(row.start_time);
  const endMin = parseHHMM(row.end_time);
  if (startMin === null || endMin === null) return null;

  const muscat = toMuscat(nowTick);
  const todayIso = `${muscat.getUTCFullYear()}-${String(muscat.getUTCMonth() + 1).padStart(2, '0')}-${String(muscat.getUTCDate()).padStart(2, '0')}`;
  if (row.date !== todayIso) return null;

  const nowMin = muscat.getUTCHours() * 60 + muscat.getUTCMinutes();

  let label;
  let tone;

  if (nowMin < startMin) {
    // Pre-window — friendly "starts in".
    const remaining = startMin - nowMin;
    label = `Starts in ${fmtHMinutes(remaining)}`;
    tone = 'bg-emerald-50 text-emerald-800 border-emerald-200';
  } else if (nowMin < endMin) {
    // Inside window — colour depends on how much is left.
    const remaining = endMin - nowMin;
    label = `${fmtHMinutes(remaining)} remaining`;
    if (remaining <= 10) tone = 'bg-red-50 text-red-800 border-red-300';
    else if (remaining <= 30) tone = 'bg-amber-50 text-amber-800 border-amber-300';
    else tone = 'bg-sky-50 text-sky-800 border-sky-200';
  } else {
    const overdue = nowMin - endMin;
    if (overdue > 60) return null; // stop shouting after 1 hour
    label = overdue === 0
      ? 'Ending now'
      : `Ended ${fmtHMinutes(overdue)} ago`;
    tone = 'bg-red-50 text-red-800 border-red-300';
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tone}`}
      data-testid={`${dataTestPrefix}-${row.id}`}
      title={`Approved window ${row.start_time || ''}–${row.end_time || ''} (Muscat)`}
    >
      <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
};

export default OvertimeSlaChip;
